import { shuffle } from '../rng/rng';
import { HAND_SIZE, PHASES } from '../constants';
import { createEmptyTurnFlags } from '../state/turnFlags';
import { getInstanceDefinition } from '../cards/cardLookup';
import { compactAndRefill } from '../dungeon/hall';

// Into the Abyss, Heart of Doom (Thunderstone Bearer): eliminated players
// (cards/effectHelpers.js's eliminatePlayer) are never handed a turn again.
// By the time this is ever called with 2+ eliminated players still mixed
// into an otherwise-live game, at least one non-eliminated player must
// exist - eliminatePlayer itself already ends the game the moment only one
// remains, so dispatch() would have refused any further action already.
function nextNonEliminatedIndex(players, fromIndex) {
  for (let i = 1; i <= players.length; i += 1) {
    const idx = (fromIndex + i) % players.length;
    if (!players[idx].eliminated) return idx;
  }
  return fromIndex;
}

// Draws up to `count` cards for `player`, reshuffling its discard pile into a
// fresh deck if the deck runs out mid-draw. Returns [updatedPlayer, nextRngState].
function drawCards(player, count, rngState) {
  let deck = [...player.deck];
  let discard = [...player.discard];
  let state = rngState;
  const drawn = [];
  while (drawn.length < count) {
    if (deck.length === 0) {
      if (discard.length === 0) break; // nothing left anywhere - draw what we can
      const [shuffled, next] = shuffle(discard, state);
      state = next;
      deck = shuffled;
      discard = [];
    }
    drawn.push(deck.shift());
  }
  return [{ ...player, deck, discard, hand: [...player.hand, ...drawn] }, state];
}

// Mandatory first step of Visit the Village / Enter the Dungeon - the single
// choke point both share (dispatchInner's VISIT_VILLAGE/ENTER_DUNGEON cases
// each call this with their own `phase`), so it's also the natural place to
// count "visited the village" / "entered the dungeon" for the end-game stats
// table.
export function revealHand(state, phase) {
  const activeIndex = state.meta.activePlayerIndex;
  const visitStatKey = phase === PHASES.VILLAGE ? 'villageVisits' : phase === PHASES.DUNGEON ? 'dungeonVisits' : null;
  const players = state.players.map((p, i) => {
    if (i !== activeIndex) return p;
    const stats = visitStatKey ? { ...p.stats, [visitStatKey]: p.stats[visitStatKey] + 1 } : p.stats;
    return { ...p, handRevealed: true, stats };
  });
  return { ...state, players, meta: { ...state.meta, phase } };
}

// Common end-of-turn: discard whatever remains in hand, unequip everything,
// reset per-turn flags, and either draw a fresh 6-card hand and pass to the
// next player, or - if this turn triggered game end - stop without drawing
// (rulebook: "game ends at the end of that turn").
export function endTurn(state) {
  const activeIndex = state.meta.activePlayerIndex;
  // Root of Corruption, Greatsword (effectRegistry/villages.js): "This
  // effect does not trigger a hall refill until the end of the turn, so it's
  // possible to take more than one monster out of the hall, but you only get
  // the options you had at the beginning of the turn." Its Dungeon ability
  // empties a hall rank via removeMonsterFromHallToDiscard without refilling
  // it (contrast defeatMonsterOutright, which refills immediately) - this
  // call is what actually catches those rank(s) up, run unconditionally
  // since it's a no-op whenever the hall already has no empty ranks (true
  // every other time endTurn runs - resolveCombat.js's own refill already
  // keeps the hall topped up turn to turn). If the refill itself reveals a
  // Raid monster needing a player choice, defer the rest of this turn's end
  // exactly like the endTurnOverride path below - dropDeadEntries
  // (handChoiceActions.js) re-invokes endTurn once that choice resolves, at
  // which point this call is a no-op and the turn actually finishes.
  const refilled = compactAndRefill(state, { playerId: state.players[activeIndex].id });
  if (refilled.meta.pendingHandChoices?.length) {
    return { ...refilled, meta: { ...refilled.meta, endTurnOverride: true } };
  }
  state = refilled;
  const player = state.players[activeIndex];
  // e.g. Curse of Shame ("draw 2 fewer cards when you draw a new hand") - read
  // before turnFlags resets, since it affects precisely the draw below.
  const handSize = Math.max(0, HAND_SIZE - (player.turnFlags.handSizeReduction || 0) + (player.turnFlags.bonusHandSizeAtEndOfTurn || 0));

  // Matt: a Thunderstone Shard's "One hero gains Strength +2" was still
  // showing up on that same physical card several turns later - see
  // cards/effectHelpers.js's grantTemporaryStrength for the full story.
  // Strip the override back off (not just decrement it - a card could have
  // stacked more than one bonus this turn) for every instance this player's
  // turn tagged, regardless of where that card ended up (hand/discard/deck -
  // doesn't matter, cards/effectHelpers.js's getInstanceDefinition reads
  // straight off this same instances map wherever the card later surfaces).
  let { instances } = state;
  for (const instanceId of player.turnFlags.temporaryStrengthBoostInstanceIds) {
    if (!instances[instanceId]) continue;
    const reverted = { ...instances[instanceId] };
    delete reverted.strength;
    instances = { ...instances, [instanceId]: reverted };
  }
  state = { ...state, instances };

  // Familiars (rulebook, "Familiars"): "At the end of any turn in which you
  // used one or more abilities on your familiar, discard the familiar." If
  // unused this turn, it stays in play untouched by the hand-clear below -
  // it is NOT swept into the discard pile along with the rest of the hand.
  const familiarUsed = player.turnFlags.familiarUsedThisTurn && player.inPlayInstanceIds.includes(player.familiarInstanceId);
  const handToProcess = familiarUsed ? [...player.hand, player.familiarInstanceId] : player.hand;

  // Filigree Amulet: "Destroy this card instead of discarding it at the end
  // of the turn." - the only ToR card with an end-of-turn destroy-instead-
  // of-discard override, so special-cased here rather than a registry
  // handler (there's no per-card end-of-turn hook to register against -
  // endTurn() otherwise unconditionally discards the whole hand). "Destroy"
  // means gone from the game entirely, same as destroyFromHand elsewhere -
  // simply excluding it from handToDiscard is enough, since hand is
  // cleared to [] regardless of where each card ends up.
  //
  // Root of Corruption, Elven Waybread: "At the end of this turn, place 1
  // hero on top of your deck." (see effectRegistry/villages.js for the
  // Waybread's own immediate destroy + turnFlags.pendingDeckPlacementInstanceIds
  // queue). Re-checks hand presence right here, at actual resolution time -
  // some OTHER effect could have moved/destroyed the chosen hero in the
  // meantime, same "recheck, don't assume" shape as pendingHeroReturns
  // below. Placed on top of `deck` (not discard), so the very next
  // drawCards call a few lines down naturally deals it into the fresh hand.
  const pendingDeckPlacementIds = player.turnFlags.pendingDeckPlacementInstanceIds.filter((id) => player.hand.includes(id));
  // Worlds Collide, Stoneguard Tanker/Juggernaut (Spoils): "Add any
  // mercenaries present to your new hand at the end of the turn." / Time
  // Bend (village card): "...Add that card to your hand after you refill
  // your hand at the end of the turn." Same shape as Elven Waybread's own
  // pendingDeckPlacementInstanceIds just above, but lands directly in the
  // NEW hand instead of on top of the deck (so it doesn't count against/
  // get reshuffled into the normal handSize draw below) - re-checked
  // against current hand right here too, same "recheck, don't assume"
  // reasoning as Waybread's own comment.
  const keptForNextHandIds = player.turnFlags.keptForNextHandInstanceIds.filter((id) => player.hand.includes(id));
  const handToDiscard = handToProcess.filter((id) => getInstanceDefinition(state, id).id !== 'village-filigree-amulet'
    && !pendingDeckPlacementIds.includes(id) && !keptForNextHandIds.includes(id));

  // Only the familiar's own entry is ever removed here - anything else
  // sitting in inPlayInstanceIds (Circle of Protection copies, an assigned
  // Mind Control monster) is untouched, exactly matching "it remains in
  // play"/"must enter the dungeon and attack that monster" persisting
  // across this same end-of-turn reset.
  const cleared = {
    ...player,
    discard: [...player.discard, ...handToDiscard],
    hand: [],
    deck: [...pendingDeckPlacementIds, ...player.deck],
    handRevealed: false,
    equippedWeapons: {},
    inPlayInstanceIds: familiarUsed
      ? player.inPlayInstanceIds.filter((id) => id !== player.familiarInstanceId)
      : player.inPlayInstanceIds,
    turnFlags: createEmptyTurnFlags(),
  };

  const gameOver = Boolean(state.meta.endTrigger);
  let { rngState } = state.meta;
  let updatedPlayer = cleared;
  if (!gameOver) {
    const [drawn, nextRng] = drawCards(cleared, handSize, rngState);
    updatedPlayer = drawn;
    rngState = nextRng;
    if (keptForNextHandIds.length) {
      updatedPlayer = { ...updatedPlayer, hand: [...updatedPlayer.hand, ...keptForNextHandIds] };
    }
    // "At the start of your turn, if you have your familiar in your hand,
    // place it face up in front of you once more. Do not draw an additional
    // card to replace it." - i.e. once it's cycled back via the normal
    // deck/discard shuffle, pull it back out of hand into the familiar zone
    // without topping the hand back up to handSize.
    if (updatedPlayer.familiarInstanceId
      && !updatedPlayer.inPlayInstanceIds.includes(updatedPlayer.familiarInstanceId)
      && updatedPlayer.hand.includes(updatedPlayer.familiarInstanceId)) {
      updatedPlayer = {
        ...updatedPlayer,
        hand: updatedPlayer.hand.filter((id) => id !== updatedPlayer.familiarInstanceId),
        inPlayInstanceIds: [...updatedPlayer.inPlayInstanceIds, updatedPlayer.familiarInstanceId],
      };
    }
  }

  const players = state.players.map((p, i) => (i === activeIndex ? updatedPlayer : p));
  // e.g. Scabbark's Spoils ("Take another turn") - the same player goes again
  // instead of play passing left. Consumed here so it only ever grants one
  // extra turn, not an infinite loop.
  const extraTurn = !gameOver && state.meta.extraTurnGranted;
  // Into the Abyss, Heart of Doom: a player eliminated mid-turn (see
  // cards/effectHelpers.js's eliminatePlayer) still finishes cycling their
  // own hand here like normal (harmless - they'll never see it again), but
  // play must skip over them when picking who goes next.
  const nextActivePlayerIndex = extraTurn ? activeIndex : nextNonEliminatedIndex(players, activeIndex);

  return {
    ...state,
    players,
    meta: {
      ...state.meta,
      rngState,
      phase: gameOver ? PHASES.GAME_OVER : PHASES.AWAITING_ACTION,
      activePlayerIndex: gameOver ? activeIndex : nextActivePlayerIndex,
      turnNumber: gameOver ? state.meta.turnNumber : state.meta.turnNumber + 1,
      gameOver,
      spoilsWindowOpen: false,
      smugglerReactWindowOpen: false,
      lastCombatResult: null,
      extraTurnGranted: false,
    },
  };
}
